import { useCallback, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { reset } from 'redux-form';

import { formName as newUserFormName, FormNewUserData } from 'dashboard/forms/FormNewUser';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import useTableState from 'dashboard/lib/hooks/useTableState';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { User, UserStatus } from 'dashboard/models/user';
import { showMessage } from 'dashboard/redux/actions/messages';
import { setUser } from 'dashboard/redux/actions/user';
import { RootState } from 'dashboard/redux/store';
import { loadAllRoles, loadRolesList } from 'dashboard/redux/thunk-actions/roles';
import { loadTeams } from 'dashboard/redux/thunk-actions/teams';
import { loadPaginatedUserList } from 'dashboard/redux/thunk-actions/user';
import DashboardUsersView from 'dashboard/views/DashboardUsersView';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useDebounced from 'lib/hooks/useDebounced';
import usePermission from 'lib/hooks/usePermission';
import { ApiEndpoint, ApiModel, ApiPayload } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardUsersRoute = (props: PropsFromRedux) => {
  const { tableState, paginationMeta, setPaginationMeta } = useTableState({});
  // Intentionally not using `useTable` here in order to support updating multiple query params at once.
  // `useTable` forces us to manage pagination state separately from other query params state.
  const [queryState, setQueryState] = useState({ ...tableState, searchTerm: '' });
  const [filterStatuses, setFilterStatuses] = useState([UserStatus.Active, UserStatus.Pending]);
  const [filterTeamIds, setFilterTeamIds] = useState([]);
  const [debouncedSetSearchTerm] = useDebounced(
    (searchTerm: string) => {
      setQueryState({
        ...queryState,
        searchTerm,
        currentPage: 1,
      });
    },
    500,
    {
      leading: false,
      trailing: true,
    },
  );
  const handlePageLinkClick = (newPageNumber: number) =>
    setQueryState({
      ...queryState,
      currentPage: newPageNumber,
    });
  const handlePageSizeClick = (newPageSize: number) =>
    setQueryState({
      ...queryState,
      pageSize: newPageSize,
    });
  const hasTeamViewPermission = usePermission(Permission.TeamView);

  useApiLoader({
    loader: () =>
      props.loadPaginatedUserList(queryState.currentPage, queryState.pageSize, setPaginationMeta, {
        omni: queryState.searchTerm,
        statuses: filterStatuses,
        teamIds: filterTeamIds,
      }),
    deps: [
      props.loadPaginatedUserList,
      queryState.currentPage,
      queryState.pageSize,
      queryState.searchTerm,
      filterStatuses,
      filterTeamIds,
    ],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load users.');
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  useApiLoader({
    loader: () => props.loadAllRoles(),
    deps: [props.loadAllRoles, props.loadRolesList],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load roles.');
      }
    },
    onNetworkError: () =>
      props.showMessage('We are experiencing network issues. Please try again.'),
  });

  useApiLoader({
    loader: () => props.loadTeams(),
    deps: [props.loadTeams],
    skip: !hasTeamViewPermission,
    onError: (error) => {
      if (isPermissionsError(error)) {
        // Intentionally omit error message
      } else {
        props.showMessage('Failed to load teams.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: addUserError, callback: addUser } = useApiCallback(
    async ({ emailAddress, roleIds }: FormNewUserData) => {
      const response = await SessionClientDashboard.postResourceCreate<User>(
        {
          attributes: {
            'email-address': emailAddress,
          },
          type: ApiModel.User,
        },
        {
          meta: {
            'role-ids': roleIds,
          },
        },
      );

      props.setUser(response.data);
      props.reset(newUserFormName);
      props.showMessage(`Successfully invited ${emailAddress}`, MessageType.Success);
    },
    [props.showMessage],
  );

  useApiErrorHandler({
    error: addUserError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else if (error.status === 409) {
        props.showMessage('This email address is already in use.', MessageType.Failure);
      } else {
        props.showMessage('Error when creating your new user.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Sorry, something went wrong. Please try again later or contact support.'),
  });

  const removeUser = async ({ id }: User) => {
    try {
      const response = await SessionClientDashboard.deleteResource<User>(ApiEndpoint.User, id);
      props.setUser(response.data);
      props.showMessage('User has been deactivated.', MessageType.Success);
    } catch (err) {
      props.showMessage('Error when removing user.');
    }
  };

  const { error: reactivateUserError, callback: reactivateUser } = useApiCallback(
    async (user: User) => {
      const response = await SessionClientDashboard.postResourceAction<User>(
        { id: user.id, type: user.type },
        'reactivate',
      );

      props.setUser(response.data);
      props.showMessage('User has been reactivated.', MessageType.Success);
    },
    [props.showMessage],
  );

  useApiErrorHandler({
    error: reactivateUserError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'We encountered an error when reactivating the user.',
          MessageType.Failure,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage('Sorry, something went wrong. Please try again later or contact support.'),
  });

  const { error: changeUserRolesError, callback: changeUserRoles } = useApiCallback(
    async (user: User, roleIds: string[]) => {
      const response = await SessionClientDashboard.updateResource<User>(
        {
          id: user.id,
          type: ApiModel.User,
        },
        {
          meta: {
            'role-ids': roleIds,
          },
        },
      );

      props.setUser(response.data);
      props.showMessage("User's role successfully updated", MessageType.Success);
    },
    [props.showMessage],
  );

  useApiErrorHandler({
    error: changeUserRolesError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage("Error when updating user's role.", MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage('Sorry, something went wrong. Please try again later or contact support.'),
  });

  const reinviteUser = async (user: User) => {
    try {
      const response = await SessionClientDashboard.postResourceAction<User>(
        { id: user.id, type: user.type },
        'resend-activation-instructions',
      );

      props.setUser(response.data);
      props.showMessage('A new activation email has been sent', MessageType.Success);
    } catch (err) {
      props.showMessage('Error resending activation instructions');
    }
  };

  const [recoveryCode, setRecoveryCode] = useState<string | undefined>(undefined);
  const clearRecoveryCode = useCallback(() => setRecoveryCode(undefined), []);

  const { error: rotateRecoveryCodeError, callback: rotateRecoveryCode } = useApiCallback(
    async (userId: string) => {
      const {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        meta: { 'recovery-code': recoveryCode },
      } = await SessionClientDashboard.postResourceAction<
        User,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        ApiPayload<unknown, ApiModel.User>,
        { 'recovery-code': string }
      >(
        {
          id: userId,
          type: ApiModel.User,
        },
        'rotate-recovery-code',
      );

      // We bypass Redux here since we don't need/want to store this value.
      setRecoveryCode(recoveryCode);
    },
    [props.showMessage],
  );

  useApiErrorHandler({
    error: rotateRecoveryCodeError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to generate recovery code.');
      }
    },
    onNetworkError: () =>
      props.showMessage('Sorry, something went wrong. Please try again later or contact support.'),
  });

  return (
    <DashboardUsersView
      handleCreateUser={addUser}
      handleReactivateUser={reactivateUser}
      handleDestroyUser={removeUser}
      reinviteUser={reinviteUser}
      changeUserRoles={changeUserRoles}
      hasSamlEnabled={props.organization.attributes['has-saml-enabled']}
      users={props.users}
      searchTerm={queryState.searchTerm}
      setSearchTerm={debouncedSetSearchTerm}
      filterStatuses={filterStatuses}
      filterTeamIds={filterTeamIds}
      setFilterStatuses={setFilterStatuses}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setFilterTeamIds={setFilterTeamIds}
      currentUser={props.currentUser}
      roles={props.roles}
      teams={props.teams}
      featureFlags={props.featureFlags}
      recoveryCode={recoveryCode}
      rotateRecoveryCode={rotateRecoveryCode}
      clearRecoveryCode={clearRecoveryCode}
      pageSize={queryState.pageSize}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      paginationMeta={paginationMeta}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      showMessage={props.showMessage}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: state.currentUser.currentUser,
    organization: state.organization.organization,
    users: state.user.users,
    featureFlags: state.featureFlags.featureFlags,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    roles: state.roles.allRoles,
    teams: state.teams.teams,
  };
};

const mapDispatchToProps = {
  loadPaginatedUserList,
  loadAllRoles,
  loadRolesList,
  loadTeams,
  setUser,
  reset,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardUsersRoute);
